'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import classnames from 'classnames';
import {
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  TextNode,
} from 'lexical';

import { Popover, PopoverContent, usePopoverContext } from '../../popover';

import styles from './EmojiPickerPlugin.module.scss';

class EmojiOption extends MenuOption {
  title: string;
  emoji: string;
  keywords: Array<string>;

  constructor(
    title: string,
    emoji: string,
    options: {
      keywords?: Array<string>;
    },
  ) {
    super(title);
    this.title = title;
    this.emoji = emoji;
    this.keywords = options.keywords || [];
  }
}
function EmojiMenuItem({
  index,
  isSelected,
  onClick,
  onMouseEnter,
  option,
}: {
  index: number;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  option: EmojiOption;
}) {
  return (
    <li
      key={option.key}
      tabIndex={-1}
      className={classnames(
        styles['emoji-menu-item'],
        isSelected && styles['emoji-menu-item--selected'],
      )}
      ref={option.setRefElement}
      role="option"
      aria-selected={isSelected}
      id={'typeahead-item-' + index}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      <span className={styles['emoji-menu-item-text']}>
        {option.emoji} {option.title}
      </span>
    </li>
  );
}

type Emoji = {
  emoji: string;
  description: string;
  category: string;
  aliases: Array<string>;
  tags: Array<string>;
  unicode_version: string;
  ios_version: string;
  skin_tones?: boolean;
};

const MAX_EMOJI_SUGGESTION_COUNT = 10;

export function EmojiPickerPlugin() {
  const [editor] = useLexicalComposerContext();
  const [queryString, setQueryString] = useState<string | null>(null);
  const [emojis, setEmojis] = useState<Array<Emoji>>([]);

  useEffect(() => {
    import('../utils/emoji-list').then((file) => setEmojis(file.default));
  }, []);

  const emojiOptions = useMemo(
    () =>
      emojis != null
        ? emojis.map(
            ({ emoji, aliases, tags }) =>
              new EmojiOption(aliases[0] || '', emoji, {
                keywords: [...aliases, ...tags],
              }),
          )
        : [],
    [emojis],
  );

  const checkForTriggerMatch = useBasicTypeaheadTriggerMatch(':', {
    minLength: 0,
    punctuation: '\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\[\\]\\\\/!%\'"~=<>:;', // allow _ and -
  });

  const options: Array<EmojiOption> = useMemo(() => {
    return emojiOptions
      .filter((option: EmojiOption) => {
        return queryString != null
          ? new RegExp(queryString, 'gi').exec(option.title) ||
            option.keywords != null
            ? option.keywords.some((keyword: string) =>
                new RegExp(queryString, 'gi').exec(keyword),
              )
            : false
          : emojiOptions;
      })
      .slice(0, MAX_EMOJI_SUGGESTION_COUNT);
  }, [emojiOptions, queryString]);

  const onSelectOption = useCallback(
    (
      selectedOption: EmojiOption,
      nodeToRemove: TextNode | null,
      closeMenu: () => void,
    ) => {
      editor.update(() => {
        const selection = $getSelection();

        if (!$isRangeSelection(selection) || selectedOption == null) {
          return;
        }

        if (nodeToRemove) {
          nodeToRemove.remove();
        }

        selection.insertNodes([$createTextNode(selectedOption.emoji)]);

        closeMenu();
      });
    },
    [editor],
  );

  return (
    <LexicalTypeaheadMenuPlugin
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      triggerFn={checkForTriggerMatch}
      options={options}
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex },
      ) => {
        if (anchorElementRef.current == null || options.length === 0) {
          return null;
        }

        return (
          <EmojiMenu
            editor={editor}
            anchorElementRef={anchorElementRef}
            options={options}
            selectedIndex={selectedIndex}
            onSelectOption={(option, index) => {
              setHighlightedIndex(index);
              selectOptionAndCleanUp(option);
            }}
            onMouseEnter={(index) => {
              setHighlightedIndex(index);
            }}
          />
        );
      }}
    />
  );
}

function EmojiMenu({
  editor,
  anchorElementRef,
  options,
  selectedIndex,
  onSelectOption,
  onMouseEnter,
}: {
  editor: ReturnType<typeof useLexicalComposerContext>[0];
  anchorElementRef: React.MutableRefObject<HTMLElement | null>;
  options: Array<EmojiOption>;
  selectedIndex: number | null;
  onSelectOption: (option: EmojiOption, index: number) => void;
  onMouseEnter: (index: number) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);

  // Set the reference to the anchor element and update open state
  useEffect(() => {
    const hasAnchor = anchorElementRef.current != null;
    const hasOptions = options.length > 0;
    setIsOpen(hasAnchor && hasOptions);
  }, [anchorElementRef, options.length]);

  if (!isOpen || options.length === 0) {
    return null;
  }

  return (
    <Popover
      placement="bottom-start"
      open={isOpen}
      onOpenChange={() => {
        // Controlled by LexicalTypeaheadMenuPlugin, so we don't handle changes
      }}
    >
      <EmojiMenuContent
        editor={editor}
        anchorElementRef={anchorElementRef}
        options={options}
        selectedIndex={selectedIndex}
        onSelectOption={onSelectOption}
        onMouseEnter={onMouseEnter}
      />
    </Popover>
  );
}

function EmojiMenuContent({
  editor,
  anchorElementRef,
  options,
  selectedIndex,
  onSelectOption,
  onMouseEnter,
}: {
  editor: ReturnType<typeof useLexicalComposerContext>[0];
  anchorElementRef: React.MutableRefObject<HTMLElement | null>;
  options: Array<EmojiOption>;
  selectedIndex: number | null;
  onSelectOption: (option: EmojiOption, index: number) => void;
  onMouseEnter: (index: number) => void;
}) {
  const context = usePopoverContext();

  // Set the reference to the anchor element from Lexical
  // Find the actual DOM element of the text node or paragraph containing the colon trigger
  useEffect(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const anchorNode = selection.anchor.getNode();
        let targetNode = anchorNode;

        // If it's a text node, get its parent paragraph element
        if ($isTextNode(anchorNode)) {
          const parent = anchorNode.getParent();
          if (parent) {
            targetNode = parent;
          }
        }

        // Get the DOM element for the target node
        const domElement = editor.getElementByKey(targetNode.getKey());
        if (domElement != null) {
          context.refs.setReference(domElement);
        }
      }
    });
  }, [editor, anchorElementRef, context.refs]);

  if (!context.open || options.length === 0) {
    return null;
  }

  return (
    <PopoverContent className={styles['emoji-menu']}>
      <ul className={styles['emoji-menu-list']}>
        {options.map((option: EmojiOption, index) => (
          <EmojiMenuItem
            key={option.key}
            index={index}
            isSelected={selectedIndex === index}
            onClick={() => {
              onSelectOption(option, index);
            }}
            onMouseEnter={() => {
              onMouseEnter(index);
            }}
            option={option}
          />
        ))}
      </ul>
    </PopoverContent>
  );
}
